import {ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {catchError, debounceTime, distinctUntilChanged, filter, finalize, map, startWith, switchMap, tap} from 'rxjs/operators';
import {FormControl} from '@angular/forms';
import {EMPTY, of, Subscription} from 'rxjs';
import {KpdCodeApiService, KpdCode, KpdCodeSearchResponse} from '@app/services/api/kpd-code-api.service';

export interface KpdCodeSelection {
  code: string;
  name: string;
}

@Component({
  selector: 'app-kpd-selector',
  templateUrl: './kpd-selector.component.html',
  styleUrl: './kpd-selector.component.scss',
  standalone: false
})
export class KpdSelectorComponent implements OnInit, OnDestroy {
  @Input() kpdCode: string = '';
  @Input() kpdName: string = '';
  @Input() placeholder: string = 'Pretraži po šifri ali nazivu...';
  @Input() hint: string = 'Obavezno za hrvatske e-račune od 1.1.2026';
  @Input() isInvalid: boolean = false;

  @Output() kpdSelected = new EventEmitter<KpdCodeSelection>();
  @Output() kpdCleared = new EventEmitter<void>();

  @ViewChild('searchInput', {static: false}) searchInput?: ElementRef;

  public searchControl = new FormControl('');
  public kpdSearchResults: KpdCodeSearchResponse | null = null;
  public isSearchingKpd: boolean = false;
  public dropdownStyle: any = null;

  private subscription: Subscription;
  private scrollListener: any = null;
  private currentSearchTerm: string = ''; // Track current search to prevent race conditions

  constructor(
    private kpdCodeApi: KpdCodeApiService,
    private cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    // Set up search stream - proper autocomplete pattern to prevent race conditions
    this.subscription = this.searchControl.valueChanges.pipe(
      map(value => value ? String(value).trim() : ''),
      debounceTime(250), // Wait for user to stop typing
      distinctUntilChanged(), // Skip if same value
      tap(inputText => {
        // CRITICAL: Set currentSearchTerm BEFORE making request
        this.currentSearchTerm = inputText;

        // Manage UI state
        const showDropdown = inputText.length >= 1;
        if (!showDropdown) {
          this.kpdSearchResults = null;
          this.dropdownStyle = null;
          this.removeScrollListener();
          this.isSearchingKpd = false;
          this.cdr.detectChanges(); // Trigger change detection when clearing
        } else {
          this.isSearchingKpd = true;
          this.calculateDropdownPosition();
          this.addScrollListener();
        }
      }),
      switchMap((value: string) => {
        // Only make API call if we have at least 1 character
        if (value.length >= 1) {
          return this.kpdCodeApi.search({
            query: value,
            limit: 50
          }).pipe(
            map(results => ({results, searchTerm: value})),
            catchError((error) => {
              console.error('Error searching KPD codes:', error);
              return of({results: null, searchTerm: value});
            }),
            finalize(() => {
              this.isSearchingKpd = false;
            })
          );
        } else {
          // Clear results for short input
          return of({results: null, searchTerm: value});
        }
      })
    ).subscribe({
      next: (data: { results: KpdCodeSearchResponse | null, searchTerm: string }) => {
        // CRITICAL: Only update if this response matches current search
        // This prevents race condition where old HTTP responses arrive late
        if (data.searchTerm === this.currentSearchTerm) {
          this.kpdSearchResults = data.results;
          // Trigger change detection for nested component hierarchies (inline-field-edit, dialogs, etc.)
          this.cdr.detectChanges();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.removeScrollListener();
  }

  selectKpdCode(code: string, name: string): void {
    this.kpdCode = code;
    this.kpdName = name;
    this.currentSearchTerm = '';
    this.searchControl.setValue('', {emitEvent: false});
    this.kpdSearchResults = null;
    this.dropdownStyle = null;
    this.removeScrollListener();
    this.kpdSelected.emit({code, name});
  }

  clearKpdCode(): void {
    this.kpdCode = '';
    this.kpdName = '';
    this.currentSearchTerm = '';
    this.searchControl.setValue('', {emitEvent: false});
    this.kpdCleared.emit();
  }

  isKpdCodeSuggested(code: string): boolean {
    const suggested = this.kpdSearchResults?.suggested;
    if (!suggested || suggested.length === 0) return false;
    return suggested.some(s => s.code === code);
  }

  hasNonSuggestedCodes(): boolean {
    const results = this.kpdSearchResults;
    if (!results?.all || results.all.length === 0) return false;
    if (!results.suggested || results.suggested.length === 0) return true;

    return results.all.some(kpd => !this.isKpdCodeSuggested(kpd.code));
  }

  /**
   * Calculate dropdown position using fixed positioning
   */
  private calculateDropdownPosition(): void {
    if (!this.searchInput) return;

    const inputElement = this.searchInput.nativeElement;
    const rect = inputElement.getBoundingClientRect();

    this.dropdownStyle = {
      position: 'fixed',
      top: `${rect.bottom}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      'z-index': '50'
    };
  }

  /**
   * Add scroll listener to update dropdown position
   */
  private addScrollListener(): void {
    if (this.scrollListener) return;

    let ticking = false;
    this.scrollListener = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.calculateDropdownPosition();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Listen to scroll events on window and all parent elements
    window.addEventListener('scroll', this.scrollListener, true);
  }

  /**
   * Remove scroll listener
   */
  private removeScrollListener(): void {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener, true);
      this.scrollListener = null;
    }
  }
}
